/**
 * CompetitorInputView — Input screen for the Competitor Intelligence module.
 */
import { useState } from 'react'
import type { CompetitorOptions, CompetitorPreset } from '@/types'

interface CompetitorInputViewProps {
  options:        CompetitorOptions
  setOptions:     (v: CompetitorOptions) => void
  presets:        CompetitorPreset[]
  presetsLoading: boolean
  onSubmit:       () => void
}

type InputMode = 'preset' | 'custom'

export default function CompetitorInputView({
  options, setOptions, presets, presetsLoading, onSubmit,
}: CompetitorInputViewProps) {
  const [mode, setMode] = useState<InputMode>(presets.length > 0 ? 'preset' : 'custom')

  const set = <K extends keyof CompetitorOptions>(key: K, value: CompetitorOptions[K]) =>
    setOptions({ ...options, [key]: value })

  const canSubmit = mode === 'preset'
    ? Boolean(options.preset)
    : Boolean(options.urls_content?.trim())

  return (
    <div className="flex flex-col items-center justify-start pt-14 pb-24 px-5 animate-fade-up">
      <div className="w-full max-w-[720px]">
        <div className="text-center mb-10">
          <h1 className="text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-navy-900 leading-tight">
            Competitor intelligence,<br className="hidden sm:block" /> automated.
          </h1>
          <p className="mt-3 text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Crawl competitor websites, extract features and generate a
            structured comparison report.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-7 p-1 bg-slate-100 rounded-xl w-fit mx-auto">
          {(['preset', 'custom'] as InputMode[]).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all
                ${mode === m
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'}`}
            >
              {m === 'preset' ? 'Use preset' : 'Custom URLs'}
            </button>
          ))}
        </div>

        {/* ── Preset selector ──────────────────────────────────────────────── */}
        {mode === 'preset' && (
          <div>
            {presetsLoading ? (
              <div className="grid sm:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton rounded-xl h-28" />
                ))}
              </div>
            ) : presets.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">
                No presets available — switch to Custom URLs mode.
              </p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-4">
                {presets.map(p => (
                  <button
                    key={p.name}
                    onClick={() => set('preset', p.name)}
                    className={`preset-card ${options.preset === p.name ? 'selected' : ''}`}
                  >
                    <p className="font-semibold text-slate-900 text-sm mb-1">{p.label}</p>
                    <p className="text-xs text-slate-500 leading-snug mb-3">{p.description}</p>
                    <span className="tag-pill">
                      {p.url_count} URL{p.url_count !== 1 ? 's' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Custom URL input ─────────────────────────────────────────────── */}
        {mode === 'custom' && (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Competitor URLs <span className="text-slate-400 font-normal">(one per line)</span>
              </label>
              <textarea
                value={options.urls_content ?? ''}
                onChange={e => set('urls_content', e.target.value)}
                placeholder={"https://competitor-a.com\nhttps://competitor-b.com\nhttps://competitor-c.com"}
                className="w-full resize-none rounded-2xl border-2 border-slate-200 px-5 py-4 text-sm
                           leading-relaxed text-slate-800 placeholder:text-slate-300 bg-white shadow-card
                           focus:outline-none focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                style={{ minHeight: 140 }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Features YAML <span className="text-slate-400 font-normal">(optional — leave blank for auto-detection)</span>
              </label>
              <textarea
                value={options.features_yaml ?? ''}
                onChange={e => set('features_yaml', e.target.value)}
                placeholder={"features:\n  - pricing\n  - integrations\n  - security"}
                className="w-full resize-none rounded-2xl border-2 border-slate-200 px-5 py-4 text-sm font-mono
                           leading-relaxed text-slate-800 placeholder:text-slate-300 bg-white shadow-card
                           focus:outline-none focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                style={{ minHeight: 100 }}
              />
            </div>
          </div>
        )}

        {/* ── Options ──────────────────────────────────────────────────────── */}
        <div className="mt-6 p-5 bg-white border border-slate-200 rounded-2xl shadow-card space-y-5">
          {/* Comparison toggle */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.comparison}
              onChange={e => set('comparison', e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand focus:ring-brand"
            />
            <div>
              <p className="text-sm font-medium text-slate-800">Include comparison table</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Generate a side-by-side comparison of all competitors vs. your company.
              </p>
            </div>
          </label>

          {/* Input company */}
          {options.comparison && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Your company name <span className="text-slate-400 font-normal">(for comparison)</span>
              </label>
              <input
                type="text"
                value={options.input_company}
                onChange={e => set('input_company', e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                           text-slate-800 placeholder:text-slate-300 focus:outline-none
                           focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
              />
            </div>
          )}

          {/* Concurrency */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Concurrency — {options.concurrency ?? 4} parallel crawlers
            </label>
            <input
              type="range" min={1} max={8} step={1}
              value={options.concurrency ?? 4}
              onChange={e => set('concurrency', Number(e.target.value))}
              className="w-full accent-brand"
            />
          </div>
        </div>

        {/* Run button */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={`w-full sm:w-auto sm:min-w-[220px] flex items-center justify-center gap-2
              px-8 py-4 rounded-2xl text-[15px] font-semibold text-white
              transition-all duration-200 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-brand focus-visible:ring-offset-2
              ${canSubmit
                ? 'bg-navy-900 hover:bg-navy-800 shadow-md hover:shadow-lg active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Run analysis
          </button>
        </div>
      </div>
    </div>
  )
}
